import React, { useCallback } from 'react';

import { eventPreventDefault } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { LinkButton, Tooltip } from '@lyearn/ui';
import useBookmarkTooltip from '@/features/bookmark/hooks/useBookmarkTooltip';
import useContentBookmark, {
  UseContentBookmarkParams,
} from '@/features/bookmark/hooks/useContentBookmark';

import { BookmarkProps } from '../types';
import { APPEARANCE_CLASSES, BOOKMARKED_CONFIG, NOT_BOOKMARKED_CONFIG } from './utils';

const BookmarkDesktop: React.FC<BookmarkProps & UseContentBookmarkParams> = (props) => {
  const { t } = useTranslation();
  const {
    appearance = 'primary',
    className,
    classes,
    tooltipProps,
    contentId,
    contentType,
    bookmarkId,
    allowUndo,
  } = props;

  const { fetching, onToggleBookmark, iconAnimationRef, updatedBookmarkId } = useContentBookmark({
    contentId,
    contentType,
    bookmarkId,
    allowUndo,
  });

  const { open, onOpen, onClose, onDisable } = useBookmarkTooltip();

  const handleOnClick = useCallback(
    (event: any) => {
      eventPreventDefault(event);
      onDisable();
      onToggleBookmark();
    },
    [onToggleBookmark, onDisable],
  );

  const { label, Icon } = updatedBookmarkId ? BOOKMARKED_CONFIG : NOT_BOOKMARKED_CONFIG;
  const stateClassName = updatedBookmarkId
    ? classes?.icon?.bookmarked
    : classes?.icon?.notBookmarked;
  const iconClassName = APPEARANCE_CLASSES[appearance];

  return (
    <Tooltip
      open={open}
      placement="top"
      title={t(label)!}
      onClose={onClose}
      onOpen={onOpen}
      {...tooltipProps}>
      <LinkButton
        className={cn(className, 'py-2 px-4')}
        data-action={updatedBookmarkId ? 'Remove Bookmark' : 'Bookmark'}
        disabled={fetching}
        onClick={handleOnClick}>
        <Icon
          className={cn(
            'h-20 w-20',
            iconClassName,
            stateClassName,
            iconAnimationRef.current,
            classes?.icon?.root,
          )}
        />
      </LinkButton>
    </Tooltip>
  );
};

export default BookmarkDesktop;
